import { useMountEffect } from "@react-hookz/web";
import { enableMapSet } from "immer";
import { isEmpty } from "lodash";
import type { FunctionComponent } from "react";
import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import RoutePaths from "./RoutePaths";
import { IsItDownBanner } from "./components/IsItDownBanner";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { Notifications } from "./components/Notifications";
import { CreateInsightDeckReportIssue } from "./components/aquilaReportIssue/CreateInsightDeckReportIssue";
import { SidePanelReportIssue } from "./components/aquilaReportIssue/SidePanelReportIssue";
import { AppContainer } from "./components/common/styled.components";
import { AppCsatFeedback } from "./components/csat/AppCsatFeedback";
import { AccountPicker } from "./components/customAccountPicker/AccountPicker";
import { TestSiteToolbar } from "./components/testSite/TestSiteToolbar";
import { TELEMETRY_ID } from "./constants/telemetry";
import { WindowMessageType } from "./constants/window";
import GlobalStyle from "./globalStyle";
import { authStore } from "./store/auth";
import { NotificationType, notificationStore } from "./store/notification";
import { windowStore } from "./store/window";
import { APPENV } from "./utils/app.environment";
import { startup } from "./utils/app.lifecycle";
import { useGetComponentHeights } from "./utils/app.utils";
import {
    isScopedToAccount,
    getAdvertiserId,
    getMarketplaceId,
    isValidAccount,
    isSingleIdentifierView,
} from "./utils/permission.utils";
import {
    asxTelemetryClient,
    logVisitMetadata,
    logWithTakt,
} from "./utils/telemetry.utils";

enableMapSet();

interface AppProps {}

asxTelemetryClient.startTimer(TELEMETRY_ID.TIMERS.APP_LOAD_TO_PAGE_CLOSE);

const ReportIssueModalsContainer = styled.div`
    z-index: 1100;
    position: relative;
`;

const App: FunctionComponent<AppProps> = () => {
    const [loading, setLoading] = useState(true);
    const [accountSwitcherPopup, setAccountSwitcherPopup] = useState(false);
    const logVisitMetadataOnce = useRef(false);
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [username] = useStore(authStore, (state) => [state.username]);
    const [setPathname] = useStore(windowStore, (state) => [state.setPathname]);
    const location = useLocation();

    useMountEffect(() => {
        setPathname(window.location.pathname);
    });

    React.useEffect(() => {
        const message = {
            type: WindowMessageType.LOCATION_CONTEXT,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
        };
        window.parent.postMessage(message, "*");
    }, [location.pathname, location.search, location.hash]);

    if (
        isScopedToAccount() &&
        isValidAccount() &&
        !logVisitMetadataOnce.current &&
        !isEmpty(username)
    ) {
        logVisitMetadata({
            tab: "Insights Hub",
            advertiserId: getAdvertiserId(),
            marketplaceId: getMarketplaceId(),
            referrer: document.referrer ?? "",
        });
        logVisitMetadataOnce.current = true;
    }

    React.useEffect(() => {
        void (async () => {
            try {
                await startup();
                setLoading(false);

                // Always log a page load
                logWithTakt(TELEMETRY_ID.PAGE_LOAD, {
                    marketplaceId: getMarketplaceId(),
                });

                // Check if we're on a template route and deep link feature is enabled
                const isTemplateRoute = window.location.pathname.match(
                    /\/domains\/[^/]+\/templates\/[^/]+/,
                );
                if (!getAdvertiserId() || !isValidAccount()) {
                    if (!isTemplateRoute && !isSingleIdentifierView()) {
                        setAccountSwitcherPopup(true);
                    }
                }
            } catch (e: any) {
                upsertNotification(e.message, NotificationType.ERROR);
            }
        })();
    }, []);

    return (
        <AppContainer
            pixelsToRemove={useGetComponentHeights()}
            data-testid="ihub-app"
        >
            <GlobalStyle />
            {!(APPENV.dev_server || APPENV.mock_server) && IsItDownBanner && (
                <IsItDownBanner />
            )}
            {TestSiteToolbar && <TestSiteToolbar />}
            {loading ? (
                <LoadingSpinner />
            ) : accountSwitcherPopup ? (
                <AccountPicker
                    onAccountSelect={(accountId) => {
                        setAccountSwitcherPopup(false);
                    }}
                    onCancel={() => setAccountSwitcherPopup(false)}
                    onClose={() => setAccountSwitcherPopup(false)}
                />
            ) : (
                <RoutePaths />
            )}
            <Notifications />
            <AppCsatFeedback />
            <ReportIssueModalsContainer>
                <SidePanelReportIssue />
                <CreateInsightDeckReportIssue />
            </ReportIssueModalsContainer>
        </AppContainer>
    );
};

export default App;
